// @flow
import type { ChildrenArray, Element } from 'react';
import React from 'react';
import {
  types as t,
  usePropsValidation,
} from '../validation/runtime-checks/index.js';
import Image from '../Image.js';
import { Text } from '../Text.js';
import { List } from '../List.js';
import { TwoColumnProof } from '../TwoColumnProof.js';
import { AlgebraicWorking } from '../AlgebraicWorking.js';
import TabularData from '../TabularData.js';
import BoxAndWhiskerPlot from '../BoxAndWhiskerPlot.js';
import { CartesianPlane } from '../CartesianPlane.js';
import { NumberLine } from '../NumberLine.js';
import { StemAndLeafPlot } from '../StemAndLeafPlot.js';
import { illustrationColor, productColor } from '../designSystem.js';
import { SolidifyAnnotatedFigure } from './SolidifyAnnotatedFigure.js';
import { Heading } from '../Heading.js';
import { SolidifyCollapsibleInternal } from './SolidifyCollapsibleInternal.js';
import { Language, English, Spanish } from '../languages.js';
import DraftImage from '../DraftImage.js';
import { SolidifyTechnologyToolScreenshot } from './SolidifyTechnologyToolScreenshot.js';

const classNamespace = `jsx-sld-worked-example-${Math.ceil(
  Math.random() * 10000,
)}`;
function className(name: string) {
  return `${classNamespace}-${name}`;
}

export function SLDWorkedExamples(props: {
  children: ChildrenArray<Element<typeof SolidifyWorkedExample>>,
}) {
  const { failedValidation } = usePropsValidation(props, SLDWorkedExamples);
  if (failedValidation) return null;

  const children = React.Children.map(props.children, (child, index) => {
    return React.cloneElement(child, { _index: index });
  });

  return (
    <div style={{ paddingBottom: 40 }}>
      <div style={{ marginLeft: 30 }}>
        <Heading>
          <Language>
            <English>Worked examples</English>
            <Spanish>Ejemplos resueltos</Spanish>
          </Language>
        </Heading>
      </div>
      {children}
    </div>
  );
}

SLDWorkedExamples.spec = {
  children: t.childrenArray(t.elementType(SolidifyWorkedExample)),
};

export function SolidifyWorkedExample(props: {
  _index: number,
  children: ChildrenArray<
    | Element<typeof SolidifyProblemStatement>
    | Element<typeof SolidifyWorkedExamplePart>
    | Element<typeof SolidifyApproach>
    | Element<typeof SolidifySolution>
    | Element<typeof SolidifyReflection>,
  >,
  FLIcon?: 'honors' | 'accessPoint',
}) {
  const { failedValidation } = usePropsValidation(props, SolidifyWorkedExample);
  const { _index, children, FLIcon } = props;
  const [problemStatement, ...rest] = React.Children.toArray(children);
  if (failedValidation) return null;

  const isMultiPart = rest[0].type === SolidifyWorkedExamplePart;
  return (
    <div style={{ color: productColor.shuttleGrey }}>
      <div
        style={{
          breakInside: 'avoid',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <header
          style={{ marginTop: _index === 0 ? 30 : 56, marginBottom: '1.25em' }}
        >
          <div style={{ position: 'absolute' }}>
            {FLIcon === 'honors' && <FLHonorsIcon />}
            {FLIcon === 'accessPoint' && <FLAccessPointIcon />}
          </div>
          <h3
            style={{
              fontSize: '1em',
              lineHeight: 1.375,
              color: illustrationColor.cloudburst,
              fontWeight: 600,
              margin: '0 0 0 30px',
            }}
          >
            <Language>
              <English>Example {_index + 1}</English>
              <Spanish>Ejemplo {_index + 1}</Spanish>
            </Language>
          </h3>
        </header>
        <style>{`       
        .${className('example-section')}:not(:last-child) {
          border-bottom: 1px solid #E5E6E8
        }
      `}</style>
        <div style={{ marginLeft: 30 }}>{problemStatement}</div>
      </div>
      {isMultiPart ? (
        // $FlowIssue cloneElement signature seems dodgy
        rest.map((child, i) => React.cloneElement(child, { _index: i }))
      ) : (
        <div
          style={{
            marginTop: 20,
            padding: '0 25px',
            backgroundColor: illustrationColor.seashell,
            borderRadius: 8,
            marginLeft: 30,
          }}
        >
          {rest}
        </div>
      )}
    </div>
  );
}

SolidifyWorkedExample.spec = {
  children: t.unionOfTypes(
    t.prefixChildrenArray(
      t.elementType(SolidifyProblemStatement),
      t.minimumCount(2, t.elementType(SolidifyWorkedExamplePart)),
    ),
    t.exactOrderChildrenArray(
      t.elementType(SolidifyProblemStatement),
      t.optional(t.elementType(SolidifyApproach)),
      t.elementType(SolidifySolution),
      t.optional(t.elementType(SolidifyReflection)),
    ),
  ),
  FLIcon: t.optional(t.unionOfValues('honors', 'accessPoint')),
};

export function SolidifySubproblemStatement(props: {|
  children: ChildrenArray<
    | Element<typeof Text>
    | Element<typeof Image>
    | Element<typeof DraftImage>
    | Element<typeof CartesianPlane>,
  >,
  _index: number,
|}) {
  const { children } = props;
  const [subProblemStatement, ...rest] = React.Children.toArray(children);
  const { failedValidation } = usePropsValidation(
    props,
    SolidifySubproblemStatement,
  );

  if (failedValidation) return null;

  const partLabelSize = 26;
  return (
    <>
      <div style={{ display: 'flex', breakInside: 'avoid' }}>
        <div
          style={{
            width: partLabelSize,
            height: partLabelSize,
            background: productColor.eggplant20,
            color: productColor.cloudburst,
            borderRadius: 4,
            fontWeight: 600,
            fontSize: 16,
            lineHeight: `${partLabelSize}px`,
            textAlign: 'center',
            display: 'inline-block',
            flexShrink: 0,
            marginTop: -2,
          }}
        >
          {String.fromCharCode(97 + props._index)}
        </div>
        <div style={{ width: 10, flexShrink: 0 }} />
        {subProblemStatement}
      </div>
      <div style={{ breakInside: 'avoid' }}>
        {rest}
        <div style={{ height: 16 }} />
      </div>
    </>
  );
}

SolidifySubproblemStatement.spec = {
  children: t.prefixChildrenArray(
    t.elementType(Text),
    t.optional(
      t.childrenArray(
        t.unionOfTypes(
          t.elementType(Image),
          t.elementType(DraftImage),
          t.elementType(CartesianPlane),
        ),
      ),
    ),
  ),
};

export function SolidifyWorkedExamplePart(props: {|
  children: ChildrenArray<
    | Element<typeof SolidifySubproblemStatement>
    | Element<typeof SolidifyApproach>
    | Element<typeof SolidifySolution>
    | Element<typeof SolidifyReflection>,
  >,
  _index: number,
  moveToNextPrintedPage?: boolean,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    SolidifyWorkedExamplePart,
  );
  const [subproblemStatement, ...rest] = React.Children.toArray(props.children);

  if (failedValidation) return null;

  return (
    <div
      style={{
        marginTop: props._index === 0 ? 28 : 48,
        pageBreakBefore: props.moveToNextPrintedPage ? 'always' : 'auto',
        marginLeft: 30,
      }}
    >
      <div>
        {/* $FlowIssue cloneElement signature seems dodgy */}
        {React.cloneElement(subproblemStatement, { _index: props._index })}
      </div>
      <div
        style={{
          marginTop: 20,
          padding: '0 25px',
          backgroundColor: illustrationColor.seashell,
          borderRadius: 8,
          color: productColor.shuttleGrey,
        }}
      >
        {rest}
      </div>
    </div>
  );
}

SolidifyWorkedExamplePart.spec = {
  children: t.exactOrderChildrenArray(
    t.elementType(SolidifySubproblemStatement),
    t.optional(t.elementType(SolidifyApproach)),
    t.elementType(SolidifySolution),
    t.optional(t.elementType(SolidifyReflection)),
  ),
  moveToNextPrintedPage: t.optional(t.boolean),
};

export function SolidifyProblemStatement(props: {
  children: ChildrenArray<
    | Element<typeof Text>
    | Element<typeof List>
    | Element<typeof Image>
    | Element<typeof TabularData>
    | Element<typeof BoxAndWhiskerPlot>
    | Element<typeof CartesianPlane>
    | Element<typeof NumberLine>
    | Element<typeof StemAndLeafPlot>
    | Element<typeof SolidifyAnnotatedFigure>
    | Element<typeof AlgebraicWorking>
    | Element<typeof TwoColumnProof>,
  >,
}) {
  const { failedValidation } = usePropsValidation(
    props,
    SolidifyProblemStatement,
  );
  if (failedValidation) return null;
  return (
    <div style={{ breakInside: 'avoid' }}>
      {props.children}
      <div style={{ height: 16 }} />
    </div>
  );
}
SolidifyProblemStatement.spec = {
  children: t.childrenArray(
    t.unionOfTypes(
      t.elementType(Text),
      t.elementType(List),
      t.elementType(Image),
      t.elementType(DraftImage),
      t.elementType(TabularData),
      t.elementType(BoxAndWhiskerPlot),
      t.elementType(CartesianPlane),
      t.elementType(NumberLine),
      t.elementType(StemAndLeafPlot),
      t.elementType(SolidifyAnnotatedFigure),
      t.elementType(AlgebraicWorking),
      t.elementType(TwoColumnProof),
    ),
  ),
};

type TaggedSectionChildren = ChildrenArray<
  | Element<typeof Text>
  | Element<typeof List>
  | Element<typeof Image>
  | Element<typeof TabularData>
  | Element<typeof BoxAndWhiskerPlot>
  | Element<typeof CartesianPlane>
  | Element<typeof NumberLine>
  | Element<typeof StemAndLeafPlot>
  | Element<typeof SolidifyAnnotatedFigure>
  | Element<typeof AlgebraicWorking>
  | Element<typeof TwoColumnProof>,
>;
const taggedSectionChildrenChecker = t.childrenArray(
  t.unionOfTypes(
    t.elementType(Text),
    t.elementType(List),
    t.elementType(Image),
    t.elementType(DraftImage),
    t.elementType(SolidifyTechnologyToolScreenshot),
    t.elementType(TabularData),
    t.elementType(BoxAndWhiskerPlot),
    t.elementType(CartesianPlane),
    t.elementType(NumberLine),
    t.elementType(StemAndLeafPlot),
    t.elementType(SolidifyAnnotatedFigure),
    t.elementType(AlgebraicWorking),
    t.elementType(TwoColumnProof),
  ),
);

export function SolidifyApproach(props: { children: TaggedSectionChildren }) {
  const { failedValidation } = usePropsValidation(props, SolidifyApproach);
  if (failedValidation) return null;
  return (
    <ExampleSection
      label={
        <Language>
          <English>Approach</English>
          <Spanish>Método</Spanish>
        </Language>
      }
      children={props.children}
    />
  );
}
SolidifyApproach.spec = { children: taggedSectionChildrenChecker };

export function SolidifySolution(props: { children: TaggedSectionChildren }) {
  const { failedValidation } = usePropsValidation(props, SolidifySolution);
  if (failedValidation) return null;
  return (
    <ExampleSection
      label={
        <Language>
          <English>Solution</English>
          <Spanish>Solución</Spanish>
        </Language>
      }
      children={props.children}
    />
  );
}
SolidifySolution.spec = { children: taggedSectionChildrenChecker };

export function SolidifyReflection(props: { children: TaggedSectionChildren }) {
  const { failedValidation } = usePropsValidation(props, SolidifyReflection);
  if (failedValidation) return null;
  return (
    <ExampleSection
      label={
        <Language>
          <English>Reflection</English>
          <Spanish>Reflexión</Spanish>
        </Language>
      }
      children={props.children}
    />
  );
}
SolidifyReflection.spec = { children: taggedSectionChildrenChecker };

function ExampleSection({
  label,
  openByDefault,
  children,
}: {|
  label: Element<typeof Language>,
  openByDefault?: boolean,
  children: React$Node,
|}) {
  return (
    <div
      className={className('example-section')}
      style={{
        breakInside: 'avoid',
        padding: '1px 0',
      }}
    >
      <SolidifyCollapsibleInternal title={label} openByDefault={openByDefault}>
        <div style={{ height: 8 }} />
        {children}
        <div style={{ height: 8 * 3 }} />
      </SolidifyCollapsibleInternal>
    </div>
  );
}

function FLHonorsIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="9" cy="9" r="9" fill="#0875BE" />
      <path
        d="M6.76309 6V8.29714H5.01867V6H3.75V12H5.01867V9.61714H6.76309V12H8.03176V6H6.76309Z"
        fill="white"
      />
      <path
        d="M12.9813 6V9.25714L10.8405 6H9.88896V12H11.1576V8.74286L13.2985 12H14.25V6H12.9813Z"
        fill="white"
      />
    </svg>
  );
}

function FLAccessPointIcon() {
  return (
    <svg
      width="19"
      height="18"
      viewBox="0 0 19 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="9.75" cy="9" r="9" fill="#0875BE" />
      <path
        d="M8.69583 11.8125H10.0849L8.19578 6.1875H6.57657L4.6875 11.8125H6.07652L6.35433 10.9286H8.41802L8.69583 11.8125ZM6.73532 9.72321L7.38618 7.65804L8.03703 9.72321H6.73532Z"
        fill="white"
      />
      <path
        d="M12.8679 6.1875H10.7645V11.8125H12.0344V10.0446H12.8679C13.9632 10.0446 14.8125 9.18482 14.8125 8.11607C14.8125 7.04732 13.9632 6.1875 12.8679 6.1875ZM12.8679 8.83929H12.0344V7.39286H12.8679C13.2568 7.39286 13.5425 7.70625 13.5425 8.11607C13.5425 8.52589 13.2568 8.83929 12.8679 8.83929Z"
        fill="white"
      />
    </svg>
  );
}
